/**
 * requiredRule is used if a field is required
 * minNumberRule is used if a field requires a min value
 * maxNumberRule is used if a field requires a max value
 * lowerCaseRule is used if a field requires a lowercase letter (unnecessary if passwordRule is used)
 * upperCaseRule is used if a field requires a uppercase letter (unnecessary if passwordRule is used)
 * minLengthRule is used if a field requires a min length (unnecessary if passwordRule is used)
 * maxLengthRule is used if a field requires a max length
 * containsNumberRule is used if a field requires a number (unnecessary if passwordRule is used)
 * passwordRule is used for password fields
 * emailRule is used for email fields
 * phoneNumberRule is used for validating phone numbers
 *    min 4 characters
 *    max 15 characters
 *    must start with one of the following characters: (, +, 0
 *    allowed consecutive characters: " ", 0-9, "-"
 *
 * use the rules in the your configuration file (e.g. formConfiguration.ts)
 *
 * ###
 * example with default rules:
 * ###
 *
 * setRules: () => {
 *    return {
 *        ...requiredRule('This field is required'),
 *        ...minNumberRule(30000, 'Please enter at least 30000'),
 *        ...maxNumberRule(10000000, 'Please enter a maximum of 10000000'),
 *        ...lowerCaseRule('Please enter a lowercase letter (a-z)'),
 *        ...uppercaseCaseRule('Please enter a uppercase letter (A-Z)'),
 *        ...passwordRule('Please enter (a-z)', 'Please enter (A-Z)', 'Please enter (0-9))', 'Password length musst be at least 10')
 *        ...emailRule('Invalid email message')
 *        ...minLengthRule('Please enter at least 10 digits or letters'),
 *        ...containsNumberRule('Please enter 0-9'),
 *        ...phoneNumberRule('Pleas enter a valid phone Number'),
 * },
 *
 * ###
 * example for custom rules that use context from react hook forms:
 * ###
 *
 * setRules: formContext => {
 *    return {
 *    ...minLoanAmount('true', formContext),
 *    };
 * },
 */

export const requiredRule = (message = 'true') => ({
  required: message
});
export const minNumberRule = (value, message = 'true') => ({
  min: {
    value,
    message
  }
});
export const maxNumberRule = (value, message = 'true') => ({
  max: {
    value,
    message: message ?? 'true'
  }
});
export const lowerCaseRule = (message = 'true') => ({
  pattern: {
    value: new RegExp(/.*[a-zäöüß]+.*/u, 'u'),
    message
  }
});
export const upperCaseRule = (message = 'true') => ({
  pattern: {
    value: new RegExp(/.*[A-ZÄÖÜẞ]+.*/u, 'u'),
    message
  }
});
export const passwordRule = (lowerCaseMessage, upperCaseMessage, numberMessage, minLengthMessage) => ({
  validate: {
    hasLowerCase: value => /(?=.*[a-zäöüß])/u.test(value) || lowerCaseMessage,
    hasUpperCase: value => /(?=.*[A-ZÄÖÜẞ])/u.test(value) || upperCaseMessage,
    hasNumber: value => /(?=.*\d)/u.test(value) || numberMessage,
    hasMinLength: value => /^.{8,}$/u.test(value) || minLengthMessage
  }
});
export const phoneNumberRule = (validationMessage = 'true') => ({
  validate: {
    isValidPhoneNumber: value => /^\(?[+0][\d ()/-]{4,15}$/u.test(value) || validationMessage
  }
});
export const emailRule = (message = 'true') => ({
  validate: {
    isValidEmail: value => (value.match(/\.\./gu) || []).length === 0 && (value.match(/@/gu) || []).length === 1 && !/([äöüÄÖÜ])/u.test(value) && /^[^\s.]\S+@\S+(\.[A-Za-z]{2,13})$/u.test(value) || message
  }
});
export const minLengthRule = (value, message = 'true') => ({
  minLength: {
    value,
    message
  }
});
export const maxLengthRule = (value, message = 'true') => ({
  maxLength: {
    value,
    message
  }
});
export const containsNumberRule = (message = 'true') => ({
  pattern: {
    value: new RegExp(/.*\\d+.*/u, 'u'),
    message
  }
});
export const notOnlyWhiteSpaceRule = (message = 'true') => ({
  validate: {
    hasOnlyWhiteSpace: value => !/^\s+$/u.test(value) || message
  }
});